import React, { FC } from 'react'
import { Box, CircularProgress, Dialog, DialogContent } from '@mui/material'

import { useSoapEditHistoryQuery } from '@/features/yakureki'
import { SoapEditHistoryItem } from '@/shared/apis/yakureki'
import { SectionHeading } from '@/components/atoms/SectionHeading'
import { MetaText } from '@/components/atoms/MetaText'
import { DialogHeader } from '@/components/atoms/DialogHeader'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'

// SOAP section 略称 → 表示 label.
const SECTION_LABELS: Record<string, string> = {
  subjectives: 'S（主観的情報）',
  objectives: 'O（客観的情報）',
  assessments: 'A（評価）',
  educational_plans: 'EP（教育計画）',
  care_plans: 'CP（ケア計画）',
  observational_plans: 'OP（観察計画）',
}

// edit_source → 表示 label (= Chip 廃止. 1 行 caption 化).
const EDIT_SOURCE_LABELS: Record<string, string> = {
  manual: '手動編集',
  manual_edit: '手動編集',
  bulk_replace: '一括置換',
  ai_chat: 'AI SOAP 更新',
  mark_unnecessary: '不要マーク',
  selection_change: '採否変更',
}

const ADD_FG = '#448361'
const ADD_BG = 'rgba(68,131,97,0.08)'
const REMOVE_FG = '#C4554D'
const REMOVE_BG = 'rgba(196,85,77,0.08)'

interface SoapEditHistoryDialogProps {
  open: boolean
  onClose: () => void
  yakurekiId?: string
}

// SOAP 編集履歴 dialog. Notion 流 (= icon 撤去 / dense border-bottom / state token color).
export const SoapEditHistoryDialog: FC<SoapEditHistoryDialogProps> = ({
  open,
  onClose,
  yakurekiId,
}) => {
  const {
    data: histories,
    isLoading,
    error,
  } = useSoapEditHistoryQuery(open ? yakurekiId : undefined)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { height: '70vh', maxHeight: 600, borderRadius: `${RADIUS.xl}px` },
      }}
    >
      <DialogHeader title="編集履歴" onClose={onClose} />
      <DialogContent sx={{ p: 0, overflowY: 'auto' }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={20} sx={{ color: 'text.secondary' }} />
          </Box>
        )}
        {error && (
          <Box sx={{ px: 3, py: 2 }}>
            <MetaText sx={{ color: '#C4554D' }}>
              履歴の取得に失敗しました
            </MetaText>
          </Box>
        )}
        {histories && histories.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <MetaText>編集履歴はありません</MetaText>
          </Box>
        )}
        {histories?.map((history) => (
          <HistoryEntry key={history.id} history={history} />
        ))}
      </DialogContent>
    </Dialog>
  )
}

const HistoryEntry: FC<{ history: SoapEditHistoryItem }> = ({ history }) => {
  const createdAt = new Date(history.created_at)
  const formattedDate = `${createdAt.getFullYear()}/${String(
    createdAt.getMonth() + 1
  ).padStart(2, '0')}/${String(createdAt.getDate()).padStart(2, '0')} ${String(
    createdAt.getHours()
  ).padStart(2, '0')}:${String(createdAt.getMinutes()).padStart(2, '0')}`
  const sourceLabel =
    EDIT_SOURCE_LABELS[history.edit_source] || history.edit_source_label

  return (
    <Box
      sx={{
        px: 3,
        py: 2,
        borderBottom: '1px solid rgba(55,53,47,0.06)',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1,
        }}
      >
        <MetaText sx={{ fontWeight: 500 }}>
          {sourceLabel}
          {history.summary && ` ・ ${history.summary}`}
        </MetaText>
        <MetaText sx={{ whiteSpace: 'nowrap' }}>{formattedDate}</MetaText>
      </Box>
      {Object.entries(history.diff_json).map(([section, diff]) => (
        <SectionDiff key={section} section={section} diff={diff} />
      ))}
    </Box>
  )
}

// diff_json は edit_source ごとに形状が異なる (= manual/bulk は added/removed/edited、
// mark_unnecessary は marked_unnecessary のみ). どの key も欠けうるため全て optional 扱いで
// default [] に落として読む (= 欠落 key への .length アクセスでクラッシュさせない).
const SectionDiff: FC<{
  section: string
  diff: {
    added?: string[]
    removed?: string[]
    edited?: { from: string; to: string }[]
    marked_unnecessary?: string[]
    deselected?: string[]
    selected?: string[]
  }
}> = ({ section, diff }) => {
  const label = SECTION_LABELS[section] || section
  const added = diff?.added ?? []
  const removed = diff?.removed ?? []
  const edited = diff?.edited ?? []
  const markedUnnecessary = diff?.marked_unnecessary ?? []
  const deselected = diff?.deselected ?? []
  const selected = diff?.selected ?? []
  const hasChange =
    added.length > 0 ||
    removed.length > 0 ||
    edited.length > 0 ||
    markedUnnecessary.length > 0 ||
    deselected.length > 0 ||
    selected.length > 0
  if (!hasChange) return null

  return (
    <Box sx={{ mb: 1 }}>
      <SectionHeading sx={{ mb: 0.5 }}>{label}</SectionHeading>
      {added.map((text, i) => (
        <DiffLine key={`a-${i}`} sign="+" fg={ADD_FG} bg={ADD_BG} text={text} />
      ))}
      {removed.map((text, i) => (
        <DiffLine
          key={`r-${i}`}
          sign="−"
          fg={REMOVE_FG}
          bg={REMOVE_BG}
          text={text}
        />
      ))}
      {/* 不要マーク = content は変えず copy から外した行. muted + 「不要」ラベルで「除外」を表す. */}
      {markedUnnecessary.map((text, i) => (
        <DiffLine
          key={`m-${i}`}
          sign="不要"
          fg={colorTokens.fg.muted}
          bg="rgba(55,53,47,0.06)"
          text={text}
        />
      ))}
      {/* 採否OFF = 行のチェックを外した (content 不変・コピー/転記から除外). muted で「除外」を表す. */}
      {deselected.map((text, i) => (
        <DiffLine
          key={`d-${i}`}
          sign="採否OFF"
          fg={colorTokens.fg.muted}
          bg="rgba(55,53,47,0.06)"
          text={text}
        />
      ))}
      {/* 採否ON = 一旦外した行を再びチェック (復帰). ADD 系 color で「戻した」を表す. */}
      {selected.map((text, i) => (
        <DiffLine
          key={`s-${i}`}
          sign="採否ON"
          fg={ADD_FG}
          bg={ADD_BG}
          text={text}
        />
      ))}
      {edited.map((edit, i) => (
        <Box key={`e-${i}`} sx={{ mb: 0.5 }}>
          <DiffLine
            sign="−"
            fg={REMOVE_FG}
            bg={REMOVE_BG}
            text={edit.from}
            sx={{ borderRadius: `${RADIUS.sm}px ${RADIUS.sm}px 0 0`, mb: 0 }}
          />
          <DiffLine
            sign="+"
            fg={ADD_FG}
            bg={ADD_BG}
            text={edit.to}
            sx={{ borderRadius: `0 0 ${RADIUS.sm}px ${RADIUS.sm}px` }}
          />
        </Box>
      ))}
    </Box>
  )
}

const DiffLine: FC<{
  sign: string
  fg: string
  bg: string
  text: string
  sx?: object
}> = ({ sign, fg, bg, text, sx }) => (
  <Box
    sx={{
      color: fg,
      bgcolor: bg,
      borderRadius: `${RADIUS.md}px`,
      px: 1,
      py: 0.25,
      mb: 0.25,
      fontSize: 14,
      lineHeight: 1.5,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      ...sx,
    }}
  >
    {sign} {text}
  </Box>
)
